import { SchedulingType } from '@wix/ambassador-restaurants-operations-v1-operation/types';

import { DispatchType } from '../../types/businessTypes';
import type { Operation } from '../../types/operation';
import type { PopulatedMenu, MenuItem } from '../../types/menu';

const LABELS = {
  vegan: {
    id: '6046e53c-4ce3-41f7-9e2a-0f7352fe4975',
    revision: '1',
    name: 'Vegan',
    icon: {
      id: 'ec845c_2306a227a54044a9999b9f5e5fdf2185.svg',
      url: 'https://static.wixstatic.com/shapes/ec845c_2306a227a54044a9999b9f5e5fdf2185.svg',
      height: 24,
      width: 24,
    },
  },
  vegetarian: {
    id: 'c05c3ba3-0917-4257-ac8b-4677a754bfbb',
    revision: '1',
    name: 'Vegetarian',
    icon: {
      id: 'ec845c_1e2d7b43595d4211a518c7876faa837c.svg',
      url: 'https://static.wixstatic.com/shapes/ec845c_1e2d7b43595d4211a518c7876faa837c.svg',
      height: 24,
      width: 24,
    },
  },
  dairyFree: {
    id: '8ae60884-8a6e-4813-8cf4-b41ae3aeac54',
    revision: '1',
    name: 'Dairy free',
    icon: {
      id: 'ec845c_03ef22be17f54eddb1499c5a56033bfe.svg',
      url: 'https://static.wixstatic.com/shapes/ec845c_03ef22be17f54eddb1499c5a56033bfe.svg',
      height: 24,
      width: 24,
    },
  },
  mild: {
    id: 'b991019d-8242-4483-ae92-f01da6675009',
    revision: '1',
    name: 'Mild',
    icon: {
      id: 'ec845c_f896174167cb464bab2989f8ca4a0091.svg',
      url: 'https://static.wixstatic.com/shapes/ec845c_f896174167cb464bab2989f8ca4a0091.svg',
      height: 24,
      width: 24,
    },
  },
  coriander: {
    id: '328a942c-afbc-4d64-9f4a-ba9a064b5a09',
    revision: '1',
    name: 'Coriander',
    icon: {
      id: 'ec845c_0e87190e65da4e77b058b6be02b23f2d.svg',
      url: 'https://static.wixstatic.com/shapes/ec845c_0e87190e65da4e77b058b6be02b23f2d.svg',
      height: 200,
      width: 200,
    },
  },
  fish: {
    id: '678d3531-4936-4060-a6fe-1b65c16569bc',
    revision: '1',
    name: 'Fish',
    icon: {
      id: 'ec845c_aec44ab07aaf49a6b76a2d53c6dea7f4.svg',
      url: 'https://static.wixstatic.com/shapes/ec845c_aec44ab07aaf49a6b76a2d53c6dea7f4.svg',
      height: 24,
      width: 24,
    },
  },
  shellfish: {
    id: 'd1cee03f-dea8-4eb2-b7eb-fa529b410a7b',
    revision: '1',
    name: 'Shellfish',
    icon: {
      id: 'ec845c_535d716f22a24816a3e2c42eee460630.svg',
      url: 'https://static.wixstatic.com/shapes/ec845c_535d716f22a24816a3e2c42eee460630.svg',
      height: 24,
      width: 24,
    },
  },
  peanuts: {
    id: 'a0cb18b7-487d-406a-9c84-70c6ef692ab6',
    revision: '1',
    name: 'Peanuts',
    icon: {
      id: 'ec845c_0a4d595588244e99abc5dad2ac26c557.svg',
      url: 'https://static.wixstatic.com/shapes/ec845c_0a4d595588244e99abc5dad2ac26c557.svg',
      height: 24,
      width: 24,
    },
  },
  raw: {
    id: '9e3b82cf-4173-4e0b-b224-b65fa8803e8a',
    revision: '1',
    name: 'Raw',
    icon: {
      id: 'ec845c_b816dc12c10541428914990c3e76828e.svg',
      url: 'https://static.wixstatic.com/shapes/ec845c_b816dc12c10541428914990c3e76828e.svg',
      height: 200,
      width: 200,
    },
  },
  treeNuts: {
    id: '9f4bd8a5-2ebf-4ada-9738-0cc5da4484f8',
    revision: '1',
    name: 'Tree nuts',
    icon: {
      id: 'ec845c_98036ed1f48440629963a81de14d47b8.svg',
      url: 'https://static.wixstatic.com/shapes/ec845c_98036ed1f48440629963a81de14d47b8.svg',
      height: 24,
      width: 24,
    },
  },
} as const;

const DEFAULT_ORDER_SETTINGS: MenuItem['orderSettings'] = {
  acceptSpecialRequests: true,
  inStock: true,
  taxGroupId: '9a5d83fd-8570-482e-81ab-cfa88942ee60',
  blockedFulfillmentMethods: [],
};

const createItem = (
  overrides: Partial<MenuItem> & Pick<MenuItem, 'id' | 'name' | 'price'>,
): MenuItem => ({
  revision: '1',
  businessLocationIds: [],
  visible: true,
  featured: false,
  additionalImages: [],
  labels: [],
  modifierGroups: [],
  orderSettings: { ...DEFAULT_ORDER_SETTINGS },
  ...overrides,
});

const appetizerItems: MenuItem[] = [
  createItem({
    id: '47595e4a-49aa-4d3d-a4fe-54c50f5eaf43',
    name: 'Bread & dips',
    description:
      'Sourdough bread accompanied by hummus, beetroot & whipped feta dips',
    image: {
      id: 'ec845c_bfcda5f9249c4229b29012f98be80c7f~mv2.jpg',
      url: 'https://static.wixstatic.com/media/ec845c_bfcda5f9249c4229b29012f98be80c7f~mv2.jpg',
      height: 5464,
      width: 8192,
    },
    labels: [LABELS.vegetarian],
    price: { amount: 4.5, currency: 'EUR' },
  }),
  createItem({
    id: '8c19a4e4-3833-4a93-95d3-81b2a4dfd3df',
    name: 'Green salad',
    description:
      'A garden-fresh salad with seasonal greens and roasted almonds',
    image: {
      id: 'ec845c_a6e150add26341928a59a4a2210d76be~mv2.jpg',
      url: 'https://static.wixstatic.com/media/ec845c_a6e150add26341928a59a4a2210d76be~mv2.jpg',
      height: 5464,
      width: 8192,
    },
    labels: [LABELS.dairyFree, LABELS.mild],
    priceVariants: {
      variants: [
        {
          id: '1c10ecae-a325-4d3a-9205-a324abfd5495',
          revision: '1',
          name: 'Small',
          priceInfo: { price: '3.50', formattedPrice: '€3.50' },
          businessLocationIds: [],
        },
        {
          id: 'febd28ce-7189-4b5a-8dfd-e59c06123b2a',
          revision: '1',
          name: 'Medium',
          priceInfo: { price: '5.50', formattedPrice: '€5.50' },
          businessLocationIds: [],
        },
      ],
    },
    price: { amount: 0, currency: 'EUR' },
  }),
  createItem({
    id: '3cf9df4d-580f-4630-b449-60e951405337',
    name: 'Tuna sashimi',
    description: 'Seared fresh tuna, fresh herbs, and a touch of chili',
    image: {
      id: 'ec845c_579903ceb1654c9ea0a22e336345dca2~mv2.jpg',
      url: 'https://static.wixstatic.com/media/ec845c_579903ceb1654c9ea0a22e336345dca2~mv2.jpg',
      height: 5464,
      width: 8192,
    },
    labels: [LABELS.coriander],
    price: { amount: 4.5, currency: 'EUR' },
  }),
];

const mainItems: MenuItem[] = [
  createItem({
    id: 'b3dfd67a-7c77-43e8-a707-38eb53850d4a',
    name: 'Hand-made ravioli',
    description:
      'Artisanal hand-made ravioli, filled with a blend of cheeses in a basil pesto sauce',
    image: {
      id: 'ec845c_e1910e857c1b42e39b394ad9de2b8870~mv2.jpg',
      url: 'https://static.wixstatic.com/media/ec845c_e1910e857c1b42e39b394ad9de2b8870~mv2.jpg',
      height: 3335,
      width: 5000,
    },
    price: { amount: 6.5, currency: 'EUR' },
  }),
  createItem({
    id: '02042f01-58ab-441c-b5db-55f51c855a62',
    name: 'Tofu skewers',
    description:
      'Grilled tofu skewers, marinated in a blend of soy and sesame with seasonal roast vegetables',
    image: {
      id: 'ec845c_0420a84b378a49e28e0b97d32c7a8282~mv2.jpg',
      url: 'https://static.wixstatic.com/media/ec845c_0420a84b378a49e28e0b97d32c7a8282~mv2.jpg',
      height: 3335,
      width: 5000,
    },
    labels: [LABELS.vegan],
    price: { amount: 7.5, currency: 'EUR' },
  }),
  createItem({
    id: 'ec225729-29ae-4a73-bdf6-f873bcabcd53',
    name: 'Fish of the day',
    description:
      'Fresh catch of the day paired with asparagus & a cream of sweet potato',
    image: {
      id: 'ec845c_5956a101ab344a2aa6181b3124131afc~mv2.jpg',
      url: 'https://static.wixstatic.com/media/ec845c_5956a101ab344a2aa6181b3124131afc~mv2.jpg',
      height: 3335,
      width: 5000,
    },
    labels: [LABELS.fish, LABELS.shellfish],
    price: { amount: 8, currency: 'EUR' },
  }),
  createItem({
    id: 'b8e179cc-f339-4ccb-afa9-d9e04d56c461',
    name: 'Peanut crusted steak',
    description:
      'Juicy, tender steak cooked to your liking, served with steamed vegetables',
    image: {
      id: 'ec845c_939605a79b964c619f0ce5c24b55f632~mv2.jpg',
      url: 'https://static.wixstatic.com/media/ec845c_939605a79b964c619f0ce5c24b55f632~mv2.jpg',
      height: 3335,
      width: 5000,
    },
    labels: [LABELS.peanuts, LABELS.raw],
    price: { amount: 8, currency: 'EUR' },
  }),
  createItem({
    id: '0517e5d4-8fd3-4b28-a743-68cf631e534a',
    name: 'Classic burger',
    description:
      'Our classic burger with lettuce, pickles, heirloom tomatoes, served with a side of fries',
    image: {
      id: 'ec845c_bfb0d41ee594410cba7737e55da0f447~mv2.jpg',
      url: 'https://static.wixstatic.com/media/ec845c_bfb0d41ee594410cba7737e55da0f447~mv2.jpg',
      height: 3335,
      width: 5000,
    },
    priceVariants: {
      variants: [
        {
          id: 'ef7dc1ed-06df-481a-8ecc-ad87ef9b6b0c',
          revision: '1',
          name: 'Mushroom',
          priceInfo: { price: '7.00', formattedPrice: '€7.00' },
          businessLocationIds: [],
        },
        {
          id: '869315e7-f1c8-428b-aff0-68bf2d7af067',
          revision: '1',
          name: 'Chicken',
          priceInfo: { price: '7.50', formattedPrice: '€7.50' },
          businessLocationIds: [],
        },
        {
          id: 'b719995f-fed6-469d-9330-4b9ea9662edf',
          revision: '1',
          name: 'Beef',
          priceInfo: { price: '9.00', formattedPrice: '€9.00' },
          businessLocationIds: [],
        },
      ],
    },
    price: { amount: 0, currency: 'EUR' },
  }),
  createItem({
    id: 'cbbf4358-292c-4e9e-a820-7e73b4c4ac00',
    name: 'Schnitzel',
    description:
      'Crisp and golden on the outside, in a herb and parmesan crumb',
    image: {
      id: 'ec845c_3cebda0fe5e34038b72c9c899b5b6d53~mv2.jpg',
      url: 'https://static.wixstatic.com/media/ec845c_3cebda0fe5e34038b72c9c899b5b6d53~mv2.jpg',
      height: 3335,
      width: 5000,
    },
    price: { amount: 4, currency: 'EUR' },
  }),
];

const dessertItems: MenuItem[] = [
  createItem({
    id: '352a961b-18b9-4f5e-8ec0-c6874f2ab496',
    name: 'Sticky date & ice cream',
    description:
      'Served with vanilla ice cream, toffee sauce & a peanut crumble',
    image: {
      id: 'ec845c_9a10415f93ca462fac597bc9a98dd82b~mv2.jpg',
      url: 'https://static.wixstatic.com/media/ec845c_9a10415f93ca462fac597bc9a98dd82b~mv2.jpg',
      height: 5464,
      width: 8192,
    },
    labels: [LABELS.vegetarian],
    price: { amount: 7, currency: 'EUR' },
  }),
  createItem({
    id: '1c1ca051-b460-4064-b7a2-5f9df47f2d3c',
    name: 'Classic cheesecake',
    description: 'Topped with a layer of raspberry jam & sliced strawberries',
    image: {
      id: 'ec845c_f7736b5d96034766874e1a7443445b0a~mv2.jpg',
      url: 'https://static.wixstatic.com/media/ec845c_f7736b5d96034766874e1a7443445b0a~mv2.jpg',
      height: 5464,
      width: 8192,
    },
    price: { amount: 6.5, currency: 'EUR' },
  }),
  createItem({
    id: '6593bef5-04e6-4841-a8a0-a9fbe8c5ebf2',
    name: 'Lemon meringue pie',
    description:
      'Zesty lemon meringue, a pistachio crumble, served with a chantilly cream',
    image: {
      id: 'ec845c_04af96bd7a6144e58344d9bb045883cc~mv2.jpg',
      url: 'https://static.wixstatic.com/media/ec845c_04af96bd7a6144e58344d9bb045883cc~mv2.jpg',
      height: 5464,
      width: 8192,
    },
    labels: [LABELS.treeNuts],
    price: { amount: 5.5, currency: 'EUR' },
  }),
  createItem({
    id: '7f2745a2-8b0b-4822-bff1-7dd7a823a677',
    name: 'Chocolate mousse',
    description: 'Our delicate, yet rich signature chocolate mousse dessert',
    image: {
      id: 'ec845c_0cdc0dfb197843fd8ed6e9d48d80c3e8~mv2.jpg',
      url: 'https://static.wixstatic.com/media/ec845c_0cdc0dfb197843fd8ed6e9d48d80c3e8~mv2.jpg',
      height: 5464,
      width: 8192,
    },
    priceVariants: {
      variants: [
        {
          id: 'eb3ee27d-f855-4272-9aa7-c6aed7445e31',
          revision: '1',
          name: 'Single serve',
          priceInfo: { price: '4.00', formattedPrice: '€4.00' },
          businessLocationIds: [],
        },
        {
          id: 'b61aef56-d714-46a6-8ad1-fc19974d7d55',
          revision: '1',
          name: 'Double serve',
          priceInfo: { price: '7.00', formattedPrice: '€7.00' },
          businessLocationIds: [],
        },
      ],
    },
    price: { amount: 0, currency: 'EUR' },
  }),
  createItem({
    id: 'd60bb131-b9b9-44f9-ad20-f19c3833f9e9',
    name: 'Carrot cake',
    description:
      'Lightly spiced carrot cake layered with cream cheese frosting',
    image: {
      id: 'ec845c_11a5dcfa74f14b1dab1c91837f1e1c38~mv2.jpg',
      url: 'https://static.wixstatic.com/media/ec845c_11a5dcfa74f14b1dab1c91837f1e1c38~mv2.jpg',
      height: 5464,
      width: 8192,
    },
    price: { amount: 5.5, currency: 'EUR' },
  }),
  createItem({
    id: '34aafd58-af34-4947-b5b6-f33820c17b4b',
    name: 'Brownie',
    description:
      'Fresh out the oven brownie filled with dark chocolate chips & walnuts',
    image: {
      id: 'ec845c_c46d8fff4c8e410c816429ce6148acd8~mv2.jpg',
      url: 'https://static.wixstatic.com/media/ec845c_c46d8fff4c8e410c816429ce6148acd8~mv2.jpg',
      height: 5464,
      width: 8192,
    },
    labels: [LABELS.dairyFree],
    price: { amount: 5, currency: 'EUR' },
  }),
];

const drinkItems: MenuItem[] = [
  createItem({
    id: 'f6222bdc-1ab2-4cd4-bf6c-8d4fca055d86',
    name: 'Healthy smoothie 1',
    description: 'Nourish your body with our selection of healthy smoothies',
    price: { amount: 3, currency: 'EUR' },
  }),
  createItem({
    id: 'e02e2782-3992-4ca5-995d-a1457510d294',
    name: 'Fresh juice',
    description:
      'A refreshing blend of freshly squeezed orange, watermelon, carrot, and ginger',
    priceVariants: {
      variants: [
        {
          id: '1c10ecae-a325-4d3a-9205-a324abfd5495',
          revision: '1',
          name: 'Small',
          priceInfo: { price: '2.00', formattedPrice: '€2.00' },
          businessLocationIds: [],
        },
        {
          id: 'febd28ce-7189-4b5a-8dfd-e59c06123b2a',
          revision: '1',
          name: 'Medium',
          priceInfo: { price: '3.00', formattedPrice: '€3.00' },
          businessLocationIds: [],
        },
        {
          id: '342f9070-7299-461b-a82d-84796ce7f8e7',
          revision: '1',
          name: 'Large',
          priceInfo: { price: '4.00', formattedPrice: '€4.00' },
          businessLocationIds: [],
        },
      ],
    },
    price: { amount: 0, currency: 'EUR' },
  }),
  createItem({
    id: '38205e6b-6c54-4176-9b79-4a926737ea8b',
    name: 'Wine',
    description: 'Choice of red, white, or rosé',
    price: { amount: 2, currency: 'EUR' },
  }),
  createItem({
    id: '639c0795-581c-4dac-a3ab-0bdf4036753c',
    name: 'Soft drink',
    description: 'Soda, Sprite, Pepsi & Diet Coke',
    price: { amount: 1.5, currency: 'EUR' },
  }),
  createItem({
    id: 'c47a77e5-1df6-4c95-a04f-7cd8bb42da33',
    name: 'Coffee',
    description: 'Locally roasted coffee, brewed in-house',
    price: { amount: 2.5, currency: 'EUR' },
  }),
  createItem({
    id: 'cd75f42f-ecd3-491c-b355-b9afbaa6b474',
    name: 'Cocktails',
    description: 'Aperol Spritz, Gin & Tonic, Mojito',
    price: { amount: 1.5, currency: 'EUR' },
  }),
];

const allItems = [
  ...appetizerItems,
  ...mainItems,
  ...dessertItems,
  ...drinkItems,
];

const dinnerMenu: PopulatedMenu = {
  id: 'bbb65621-6338-4837-8fdd-aebc654523e0',
  revision: '1',
  name: 'Dinner Menu',
  description: '',
  visible: true,
  sectionIds: [
    'a1e247ea-0c57-498e-a9be-4044ed48bda8',
    '9ea7135d-c232-437f-a9c3-33dd87265542',
    'be9330c1-c3c0-4f76-96cc-b69eac8d88da',
    '70f12b79-cd30-4750-91ab-1021a16cbbfa',
  ],
  sections: [
    {
      id: 'a1e247ea-0c57-498e-a9be-4044ed48bda8',
      revision: '1',
      name: 'Appetizers',
      description: 'These dishes are great for sharing',
      visible: true,
      itemIds: appetizerItems.map((item) => item.id!),
      size: appetizerItems.length,
    },
    {
      id: '9ea7135d-c232-437f-a9c3-33dd87265542',
      revision: '1',
      name: 'Mains',
      description:
        'A diverse range of flavorful dishes which are all sourced daily and locally',
      visible: true,
      itemIds: mainItems.map((item) => item.id!),
      size: mainItems.length,
    },
    {
      id: 'be9330c1-c3c0-4f76-96cc-b69eac8d88da',
      revision: '1',
      name: 'Desserts',
      description: 'Our desserts are made in house by our pastry chef',
      visible: true,
      itemIds: dessertItems.map((item) => item.id!),
      size: dessertItems.length,
    },
    {
      id: '70f12b79-cd30-4750-91ab-1021a16cbbfa',
      revision: '1',
      name: 'Drinks',
      description: '',
      visible: true,
      itemIds: drinkItems.map((item) => item.id!),
      size: drinkItems.length,
    },
  ],
  size: allItems.length,
};

const brunchMenu: PopulatedMenu = {
  id: 'aac54510-5227-4726-8fdd-bfcd765634f1',
  revision: '1',
  name: 'Brunch Menu',
  description: 'Weekend brunch favorites',
  visible: true,
  sectionIds: ['a1e247ea-0c57-498e-a9be-4044ed48bda8'],
  sections: [
    {
      id: 'a1e247ea-0c57-498e-a9be-4044ed48bda8',
      revision: '1',
      name: 'Appetizers',
      description: 'These dishes are great for sharing',
      visible: true,
      itemIds: appetizerItems.map((item) => item.id!),
      size: appetizerItems.length,
    },
  ],
  size: appetizerItems.length,
};

export const getMockMenus = (): PopulatedMenu[] => [dinnerMenu];

export const getMockAllMenus = (): PopulatedMenu[] => [dinnerMenu, brunchMenu];

export const getMockOperation = (): Operation => ({
  id: '48605ac5-53fc-49b4-96b2-87f7636f5ce0',
  name: 'Ordering page 1',
  enabled: true,
  fulfillmentIds: [
    '12590791-d0d9-418f-a226-a7dcfd37b507',
    '71831b22-a043-4998-a10d-01bf9f2472f2',
    'bace7396-8cc4-4e84-9be9-0aa87e7520e4',
  ],
  asapOptions: { maxInMinutes: 30 },
  schedulingType: SchedulingType.ASAP,
  allowAsapFutureHandling: true,
  businessDaysAheadHandlingOptions: 0,
  defaultDispatchType: DispatchType.PICKUP,
  operationGroupId: '1767f03d-2c3c-4b22-ab40-a79d8df7266b',
  locationId: '',
});

export const getMockLocationOperations = (): Operation[] => [
  {
    ...getMockOperation(),
    id: 'op-london',
    name: 'London ordering',
    locationId: 'loc-london',
    locationDetails: { name: 'London' },
  },
  {
    ...getMockOperation(),
    id: 'op-kyiv',
    name: 'Kyiv ordering',
    locationId: 'loc-kyiv',
    locationDetails: { name: 'Kyiv' },
  },
  {
    ...getMockOperation(),
    id: 'op-tel-aviv',
    name: 'Tel Aviv ordering',
    locationId: 'loc-tel-aviv',
    locationDetails: { name: 'Tel Aviv' },
  },
];

export const getMockItemMap = (): Record<string, MenuItem> => {
  const map: Record<string, MenuItem> = {};

  allItems.forEach((item) => {
    if (item.id) {
      map[item.id] = item;
    }
  });

  return map;
};
